import { ElementaryExchange, ExchangesResponse, IntermediateExchange } from 'hooks/data/datasets/exchanges/types';

export type ExchangeSection = 'input' | 'output';

export type MergedExchange = {
  key: string;
  name: string;
  unit: string;
  group: string;
  amounts: [number | undefined, number | undefined];
};

export type ExchangeComparison = {
  inputs: MergedExchange[];
  outputs: MergedExchange[];
};

const INPUT_GROUPS = new Set(['From technosphere', 'From environment']);

export const getExchangeSection = (group: string): ExchangeSection => (INPUT_GROUPS.has(group) ? 'input' : 'output');

type ExchangeRow = Pick<IntermediateExchange | ElementaryExchange, 'name' | 'unit' | 'group' | 'amount'>;

const allExchanges = (exchanges?: ExchangesResponse): ExchangeRow[] =>
  exchanges ? [...(exchanges.intermediateExchange ?? []), ...(exchanges.elementaryExchange ?? [])] : [];

export const buildExchangeComparison = (a?: ExchangesResponse, b?: ExchangesResponse): ExchangeComparison => {
  const merged = new Map<string, MergedExchange>();

  const add = ({ name, unit, group, amount }: ExchangeRow, index: 0 | 1) => {
    // Supplier and compartment are left out on purpose: rows show only the name, so splitting by them reads as duplicates.
    const key = `${group}|${name}|${unit}`;
    const existing = merged.get(key);
    if (existing) {
      existing.amounts[index] = (existing.amounts[index] ?? 0) + amount;
      return;
    }
    const amounts: [number | undefined, number | undefined] = [undefined, undefined];
    amounts[index] = amount;
    merged.set(key, { key, name, unit, group, amounts });
  };

  allExchanges(a).forEach((exchange) => add(exchange, 0));
  allExchanges(b).forEach((exchange) => add(exchange, 1));

  const all = [...merged.values()].sort((x, y) => x.name.localeCompare(y.name));

  return {
    inputs: all.filter((exchange) => getExchangeSection(exchange.group) === 'input'),
    outputs: all.filter((exchange) => getExchangeSection(exchange.group) === 'output'),
  };
};

export const isCommonExchange = (exchange: MergedExchange): boolean =>
  exchange.amounts[0] !== undefined && exchange.amounts[1] !== undefined;

export type ExchangesEmptyReason = 'no-exchanges' | 'no-common-exchanges';

export const getEmptyReason = (totalCount: number, visibleCount: number): ExchangesEmptyReason | null => {
  if (totalCount === 0) return 'no-exchanges';
  if (visibleCount === 0) return 'no-common-exchanges';

  return null;
};
